// A panel that rises from the bottom of a phone (Book a visit, Log completed work, Job done) stays above the keyboard
// (Liam's iPhone, 13 Oct 2026, on Job done: "Cant access the note section here to type it keyboard covers it"). An
// iPhone does not make the page shorter when its keyboard comes up, so a panel standing on the bottom of the screen
// has its lower half behind the keyboard. While a box in the panel is being typed in, the panel stands on the top of
// the keyboard instead, no taller than the part of the screen left, and the box is scrolled into sight inside it,
// clear of the panel's pinned heading and its pinned Save at the foot (which would otherwise sit over it). Nothing
// changes on a computer, or once the keyboard goes. The quick sheets (customer-sheets.ts sheet: the add box, prices,
// + Add mobile) and Add another job do the same.

// Less than this is the browser's own bars moving, not a keyboard.
const KEYBOARD = 120

// A box ready to type in as its sheet opens: on a computer only (a fine pointer that can hover), as Help's question box
// (26 Sep 2026). On a phone the cursor in a box brings the keyboard up at once, and an iPhone then pans the page to where
// that box is at that moment: still rising from the bottom, far below the screen, so the page was panned past the
// sheet and the screen was left white over the keyboard (Liam's iPhone, 20 Oct 2026, "+ Add an address" on Emma's
// visit: he had to scroll up to find her details and Save). On a phone the box is tapped once the sheet is up.
export function cursorOnOpen(box: HTMLInputElement | HTMLTextAreaElement | null | undefined, opts: { select?: boolean } = {}): void {
  if (!box || typeof matchMedia !== 'function' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return
  box.focus()
  if (opts.select) box.select()
}

export function keepAboveKeyboard(sheet: HTMLElement, opts: { inline?: boolean; onFit?: () => void } = {}): () => void {
  const vv = window.visualViewport
  if (!vv) return () => {}
  // Once per pop-up: its own call, or the watch below for one that has none.
  if (sheet.dataset.keyboard === '1') return () => {}
  sheet.dataset.keyboard = '1'
  const typing = (): HTMLElement | null => {
    const a = document.activeElement
    return a instanceof HTMLElement && sheet.contains(a) && a.matches('input, textarea, select, [contenteditable="true"]') ? a : null
  }
  let frame = 0
  const fit = (): void => {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      // The keyboard: the page's height less what can still be seen. The panel stands on the bottom of what can be seen,
      // wherever the iPhone has panned the page to show the box (even past the page's own bottom: then it stands below
      // it), so its heading, the box and Save are always in sight (Liam's iPhone, 20 Oct 2026: with the page panned
      // more than a little, it was taken for no keyboard and the panel was left where it could not be seen).
      const page = document.documentElement.clientHeight || window.innerHeight
      const keyboard = Math.round(page - vv.height)
      const under = Math.round(page - vv.height - vv.offsetTop)
      const box = typing()
      if (keyboard < KEYBOARD || !box) {
        sheet.style.removeProperty('bottom')
        sheet.style.removeProperty('max-height')
        sheet.classList.remove('sheet-typing')
        if (opts.inline) sheet.style.removeProperty('top')
        opts.onFit?.()
        return
      }
      // While typing the panel can say so: one with its own scrolling middle (New customer) then scrolls as a whole, so
      // the boxes under the one being typed in still show (Liam's iPhone, 26 Oct 2026: "It cuts of the form").
      sheet.classList.add('sheet-typing')
      sheet.style.bottom = `${under}px`
      sheet.style.maxHeight = `${Math.floor(vv.height * (opts.inline ? 1 : 0.94))}px`
      if (opts.inline) sheet.style.top = `${vv.offsetTop}px`
      opts.onFit?.()
      intoSight(box)
    })
  }
  // Between the pinned heading and the pinned foot, the top of the box first when it is taller than the gap.
  const intoSight = (box: HTMLElement): void => {
    const area = sheet.getBoundingClientRect()
    let top = area.top, bottom = area.bottom
    for (const bar of sheet.querySelectorAll<HTMLElement>('header, footer, .pin-foot, .ask-day-actions, .nj-outcomes, .people-add-acts')) {
      if (getComputedStyle(bar).position !== 'sticky') continue
      const r = bar.getBoundingClientRect()
      if (r.top - area.top < area.bottom - r.bottom) top = Math.max(top, r.bottom)
      else bottom = Math.min(bottom, r.top)
    }
    top += 8
    bottom -= 8
    const r = box.getBoundingClientRect()
    if (r.bottom > bottom) sheet.scrollTop += Math.min(r.bottom - bottom, r.top - top)
    else if (r.top < top) sheet.scrollTop -= top - r.top
  }
  vv.addEventListener('resize', fit)
  vv.addEventListener('scroll', fit)
  sheet.addEventListener('focusin', fit)
  sheet.addEventListener('focusout', fit)
  return () => {
    cancelAnimationFrame(frame)
    vv.removeEventListener('resize', fit)
    vv.removeEventListener('scroll', fit)
    sheet.removeEventListener('focusin', fit)
    sheet.removeEventListener('focusout', fit)
    if (opts.inline) {
      sheet.classList.remove('sheet-typing')
      for (const prop of ['top', 'bottom', 'max-height']) sheet.style.removeProperty(prop)
      delete sheet.dataset.keyboard
    }
  }
}

// Every pop-up stands above the keyboard while a box in it is typed in, not only the ones that ask (26 Oct 2026: with their
// main button pinned to the foot, a box low in a pop-up would otherwise sit behind the keyboard or the button). Pop-ups are
// added straight to the page's body; one that already looks after itself is left alone.
let watchingDialogs = false
export function keepEveryDialogAboveKeyboard(): void {
  if (watchingDialogs || typeof MutationObserver === 'undefined' || !window.visualViewport) return
  watchingDialogs = true
  const take = (d: HTMLElement): void => {
    if (d.dataset.keyboard === '1') return
    const free = keepAboveKeyboard(d)
    d.addEventListener('close', () => { free(); delete d.dataset.keyboard }, { once: true })
  }
  for (const d of Array.from(document.querySelectorAll<HTMLElement>('body > dialog'))) take(d)
  new MutationObserver((records) => {
    for (const r of records) for (const n of Array.from(r.addedNodes)) if (n instanceof HTMLDialogElement) take(n)
  }).observe(document.body, { childList: true })
  // ...and one kept inside the page (Today's stop pop-ups live in their job's row; GPT-6.1): taken on as a box in it is
  // tapped, before the keyboard comes up.
  document.addEventListener('focusin', (e) => {
    const d = (e.target as HTMLElement | null)?.closest?.('dialog')
    if (d instanceof HTMLDialogElement) take(d)
  }, true)
}
